import { useSimulationStore } from '../../stores/simulationStore';
import type { AisTargetConfig, AisShipType } from '../../types';

const SHIP_TYPES: { value: AisShipType; label: string }[] = [
  { value: 'cargo', label: 'Cargo' },
  { value: 'tanker', label: 'Tanker' },
  { value: 'passenger', label: 'Passenger' },
  { value: 'fishing', label: 'Fishing' },
  { value: 'sailing', label: 'Sailing' },
  { value: 'pleasure_craft', label: 'Pleasure' },
  { value: 'towing', label: 'Towing' },
  { value: 'military', label: 'Military' },
  { value: 'high_speed_craft', label: 'HSC' },
  { value: 'other', label: 'Other' },
];

const SHIP_TYPE_COLORS: Record<string, string> = {
  cargo: '#22c55e',
  tanker: '#ef4444',
  passenger: '#3b82f6',
  fishing: '#f59e0b',
  sailing: '#a855f7',
  pleasure_craft: '#06b6d4',
  military: '#6b7280',
  towing: '#f97316',
  high_speed_craft: '#ec4899',
  dredging: '#78716c',
  diving: '#14b8a6',
  other: '#9ca3af',
};

function getNextMmsi(existing: number[]): number {
  for (let i = 1; i <= 99; i++) {
    const mmsi = 200000000 + i;
    if (!existing.includes(mmsi)) return mmsi;
  }
  // All 99 slots taken — return next above range (unlikely with 10-target limit)
  return 200000100;
}

function createDefaultTarget(existingMmsis: number[]): Partial<AisTargetConfig> {
  return {
    name: '',
    mmsi: getNextMmsi(existingMmsis),
    call_sign: '',
    ship_type: 'cargo',
    imo_number: 0,
    length: 180,
    beam: 28,
    draft: 10,
    speed_knots: 12,
    start_lat: 0,
    start_lon: 0,
    end_lat: 0,
    end_lon: 0,
  };
}

// Form reads/writes directly to aisEditingTarget in the store.
// No local useState — immune to component remounts from parent re-renders.
function AddTargetForm() {
  const { addAisTarget, aisTargets, aisEditingTarget, setAisEditingTarget, mapMode, setMapMode } =
    useSimulationStore();

  // aisEditingTarget IS the form state — single source of truth
  if (!aisEditingTarget) return null;

  const form = aisEditingTarget;
  const existingMmsis = aisTargets.map((t) => t.config.mmsi);

  const handleChange = (field: keyof AisTargetConfig, value: string | number) => {
    setAisEditingTarget({ ...form, [field]: value });
  };

  const hasStartEnd =
    form.start_lat !== 0 && form.start_lon !== 0 && form.end_lat !== 0 && form.end_lon !== 0;
  const canSubmit = form.name && form.name.length > 0 && hasStartEnd;

  const handleSubmit = () => {
    if (!canSubmit) return;
    addAisTarget(form as AisTargetConfig);
    // addAisTarget sets aisEditingTarget to null, then we re-open with fresh defaults
    setTimeout(() => {
      setAisEditingTarget(createDefaultTarget([...existingMmsis, form.mmsi!]));
    }, 0);
  };

  return (
    <div className="space-y-3 p-3 bg-nautical-dark rounded-lg border border-nautical-border">
      <h4 className="text-xs font-semibold text-gray-400 uppercase">Add Target</h4>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-500">Name</label>
          <input
            type="text"
            className="input text-sm"
            placeholder="CARGO ONE"
            value={form.name ?? ''}
            onChange={(e) => handleChange('name', e.target.value.toUpperCase())}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Call Sign</label>
          <input
            type="text"
            className="input text-sm"
            placeholder="SIM001"
            value={form.call_sign ?? ''}
            onChange={(e) => handleChange('call_sign', e.target.value.toUpperCase())}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-500">Ship Type</label>
          <select
            className="input text-sm"
            value={form.ship_type ?? 'cargo'}
            onChange={(e) => handleChange('ship_type', e.target.value)}
          >
            {SHIP_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Speed (kn)</label>
          <input
            type="number"
            className="input text-sm"
            value={form.speed_knots ?? 12}
            onChange={(e) => handleChange('speed_knots', parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-xs text-gray-500">Length (m)</label>
          <input
            type="number"
            className="input text-sm"
            value={form.length ?? 180}
            onChange={(e) => handleChange('length', parseFloat(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Beam (m)</label>
          <input
            type="number"
            className="input text-sm"
            value={form.beam ?? 28}
            onChange={(e) => handleChange('beam', parseFloat(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Draft (m)</label>
          <input
            type="number"
            className="input text-sm"
            value={form.draft ?? 10}
            onChange={(e) => handleChange('draft', parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>

      {/* Start/End point buttons */}
      <div className="flex space-x-2">
        <button
          className={`flex-1 py-2 px-2 rounded-md text-xs transition-colors ${
            mapMode === 'aisStart'
              ? 'bg-red-600 text-white'
              : form.start_lat !== 0
                ? 'bg-nautical-panel text-green-400 border border-green-600/30'
                : 'bg-nautical-panel text-gray-400 border border-dashed border-nautical-border hover:border-red-500'
          }`}
          onClick={() => setMapMode(mapMode === 'aisStart' ? 'pan' : 'aisStart')}
        >
          {form.start_lat !== 0
            ? `Start: ${form.start_lat!.toFixed(4)}, ${form.start_lon!.toFixed(4)}`
            : 'Set Start Point'}
        </button>
        <button
          className={`flex-1 py-2 px-2 rounded-md text-xs transition-colors ${
            mapMode === 'aisEnd'
              ? 'bg-green-600 text-white'
              : form.end_lat !== 0
                ? 'bg-nautical-panel text-green-400 border border-green-600/30'
                : 'bg-nautical-panel text-gray-400 border border-dashed border-nautical-border hover:border-green-500'
          }`}
          onClick={() => setMapMode(mapMode === 'aisEnd' ? 'pan' : 'aisEnd')}
        >
          {form.end_lat !== 0
            ? `End: ${form.end_lat!.toFixed(4)}, ${form.end_lon!.toFixed(4)}`
            : 'Set End Point'}
        </button>
      </div>

      <button
        className={`w-full py-2 rounded-md text-sm font-medium transition-colors ${
          canSubmit
            ? 'bg-marine-600 text-white hover:bg-marine-500'
            : 'bg-nautical-darker text-gray-500 cursor-not-allowed'
        }`}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        Add Target
      </button>
    </div>
  );
}

export { SHIP_TYPE_COLORS };

export default function AisTargetPanel() {
  const { aisTargets, removeAisTarget, clearAisTargets, aisEditingTarget, setAisEditingTarget, setMapMode } =
    useSimulationStore();

  const isAdding = aisEditingTarget !== null;
  const existingMmsis = aisTargets.map((t) => t.config.mmsi);

  const handleToggleForm = () => {
    if (isAdding) {
      // Cancel — clear form and reset map mode
      setAisEditingTarget(null);
      setMapMode('pan');
    } else {
      // Open — initialize with defaults
      setAisEditingTarget(createDefaultTarget(existingMmsis));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-300">{aisTargets.length}/10 targets</span>
        <div className="flex space-x-2">
          {aisTargets.length > 0 && (
            <button
              className="text-xs text-red-400 hover:text-red-300 underline"
              onClick={clearAisTargets}
            >
              Clear All
            </button>
          )}
          {(aisTargets.length < 10 || isAdding) && (
            <button
              className="text-xs text-marine-400 hover:text-marine-300"
              onClick={handleToggleForm}
            >
              {isAdding ? 'Cancel' : '+ Add Target'}
            </button>
          )}
        </div>
      </div>

      {isAdding && <AddTargetForm />}

      {/* Target list */}
      {aisTargets.map((target) => (
        <div
          key={target.config.mmsi}
          className="p-3 bg-nautical-dark rounded-lg border border-nautical-border"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: SHIP_TYPE_COLORS[target.config.ship_type] || '#9ca3af' }}
              />
              <span className="text-sm font-medium text-gray-200">{target.config.name}</span>
            </div>
            <button
              className="text-xs text-red-400 hover:text-red-300"
              onClick={() => removeAisTarget(target.config.mmsi)}
            >
              Remove
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1 text-xs text-gray-500">
            <span>MMSI: {target.config.mmsi}</span>
            <span>SOG: {target.speed_over_ground.toFixed(1)} kn</span>
            <span>COG: {target.course_over_ground.toFixed(0)}</span>
          </div>
          <div className="mt-2">
            <div className="h-1.5 bg-nautical-darker rounded-full overflow-hidden">
              <div
                className="h-full bg-marine-600 transition-all"
                style={{ width: `${target.progress_percent}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {target.is_complete ? 'Arrived' : `${target.progress_percent.toFixed(1)}%`}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
